import { useState, useCallback } from 'react'
import { useEnumsQuery } from '@/api/accommodation/hooks'
import { type LocationSearchParams, useLocationSearchParams } from '../../hooks/useLocatiomSearchParams'
import { countActiveFilters, formatSearchSummary } from '../utils/searchSummary'
import { parseFacilityKeys } from '../utils/facilityOptions'
import { trackSearchFiltersApplied } from '@/lib/track'
import { formatShortDate } from '@/lib/utils'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { SearchFiltersFormMobile } from './SearchFiltersForm'
import { ListFilterIcon, UserIcon, BabyIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type MobileFilterView = 'filters' | 'region' | 'label' | 'facility'

function activeFiltersLabel(count: number): string {
  if (count === 1) {
    return '1 aktivní filtr'
  }
  return count < 5 ? `${count} aktivní filtry` : `${count} aktivních filtrů`
}

export function MobileSearchSummary() {
  const { data: enums } = useEnumsQuery()
  const { filters, setFilters } = useLocationSearchParams()
  const [isOpen, setIsOpen] = useState(false)
  const [localFilters, setLocalFilters] = useState<LocationSearchParams>(filters)
  const [view, setView] = useState<MobileFilterView>('filters')

  const summary = formatSearchSummary(filters, enums?.regions)
  const selectedLabel = filters.labelId ? enums?.labels.find((l) => l.id === filters.labelId)?.name : undefined
  const facilityCount = parseFacilityKeys(filters.facility).length
  const activeFilterCount = countActiveFilters(filters)
  const dateString = filters.fromDate
    ? filters.toDate
      ? `${formatShortDate(filters.fromDate)} – ${formatShortDate(filters.toDate)}`
      : formatShortDate(filters.fromDate)
    : undefined

  const updateLocalFilters = useCallback(
    (updates: Partial<LocationSearchParams>, commit = false) => {
      const newState = { ...localFilters, ...updates }
      setLocalFilters(newState)
      if (commit) {
        setFilters(newState)
      }
    },
    [localFilters, setFilters],
  )

  const commitFilters = useCallback(() => {
    trackSearchFiltersApplied(localFilters)
    setFilters(localFilters)
    setIsOpen(false)
  }, [localFilters, setFilters])

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setLocalFilters(filters)
      setView('filters')
    }
    setIsOpen(open)
  }

  return (
    <>
      <Button
        onClick={() => handleOpenChange(true)}
        aria-label={
          activeFilterCount > 0 ? `Upravit hledání, ${activeFiltersLabel(activeFilterCount)}` : 'Upravit hledání'
        }
        className="flex h-auto w-full cursor-pointer items-center gap-3 rounded-full border border-slate-200 bg-white py-3 pr-3 pl-6 text-left shadow-lg shadow-slate-600/20 hover:bg-white"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-0">
          <p className="flex min-w-0 items-center text-lg font-semibold text-slate-900">
            <span className="truncate">{summary.location}</span>
            <span className="mt-0.5 ml-2 inline-flex shrink-0 items-center gap-0.5 text-sm font-semibold text-slate-500">
              <UserIcon className="h-3.5 w-3.5 fill-current stroke-0" />
              {filters.beds}
              {filters.cribs > 0 && (
                <>
                  <BabyIcon className="ml-1 h-3.5 w-3.5 fill-current stroke-0" />
                  {filters.cribs}
                </>
              )}
            </span>
          </p>
          <p className="flex min-w-0 items-center gap-2 overflow-hidden text-sm whitespace-nowrap text-slate-500">
            {selectedLabel && (
              <>
                <span>{selectedLabel}</span>
                <span>&bull;</span>
              </>
            )}
            <span>{dateString ?? 'Kdykoliv'}</span>
            {facilityCount > 0 && (
              <>
                <span>&bull;</span>
                <span>{facilityCount}&times;&nbsp;vybavení</span>
              </>
            )}
          </p>
        </div>
        {/* Styled part of the one summary button, not a nested control. */}
        <span
          className={cn(
            'flex h-11 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-semibold',
            activeFilterCount > 0
              ? 'border-red-200 bg-red-50 text-red-600'
              : 'border-transparent bg-slate-100 text-slate-900',
          )}
        >
          <ListFilterIcon aria-hidden className="size-4" />
          {activeFilterCount > 0 ? `Filtry · ${activeFilterCount}` : 'Filtry'}
        </span>
      </Button>

      <Drawer open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerContent className="h-auto max-h-[calc(100dvh-2rem)] gap-0">
          <DrawerHeader className="relative flex-row items-center gap-2">
            <DrawerTitle
              className={cn(
                'transition-opacity duration-200',
                view === 'region' ? 'opacity-100' : 'absolute opacity-0',
              )}
            >
              Výběr lokality
            </DrawerTitle>
            <DrawerTitle
              className={cn('transition-opacity duration-200', view === 'label' ? 'opacity-100' : 'absolute opacity-0')}
            >
              Typ pobytu
            </DrawerTitle>
            <DrawerTitle
              className={cn(
                'transition-opacity duration-200',
                view === 'facility' ? 'opacity-100' : 'absolute opacity-0',
              )}
            >
              Vybavení
            </DrawerTitle>
            <DrawerTitle
              className={cn(
                'transition-opacity duration-200',
                view === 'filters' ? 'opacity-100' : 'absolute opacity-0',
              )}
            >
              Upravit hledání
            </DrawerTitle>
          </DrawerHeader>

          <div className="overflow-y-auto px-4">
            <SearchFiltersFormMobile
              localFilters={localFilters}
              filters={filters}
              updateLocalFilters={updateLocalFilters}
              commitFilters={commitFilters}
              view={view}
              onViewChange={setView}
            />
          </div>
        </DrawerContent>
      </Drawer>
    </>
  )
}
